/**
 * /usage — org AI wallet, daily token usage, and per-prompt history.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AppBar, Box, Button, Card, CardContent, Container, Stack, Toolbar, Typography } from '@mui/material';
import { ArrowBack as BackIcon, Psychology as AgentIcon } from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import useAuth from '../hooks/useAuth';
import { listProjects } from '../services/project.service';
import { browserTimeZone, getUsagePrompt, getUsageSummary, getUsageTimeseries, getWallet, listUsagePrompts } from '../services/usage.service';
import type { UsageQuery } from '../types/usage';
import { WalletCard } from '../components/usage/WalletCard';
import { UsageFilters, defaultFilters, type UsageFilterState } from '../components/usage/UsageFilters';
import { UsageChart } from '../components/usage/UsageChart';
import { SummaryTiles } from '../components/usage/SummaryTiles';
import { PromptsTable } from '../components/usage/PromptsTable';

const ADMIN_ROLES = ['admin', 'owner'];

const UsagePage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const linkedPromptId = searchParams.get('prompt');

  const selfUserId = user?.user_id || '';
  const isAdmin = useMemo(() => {
    const org = user?.organizations?.find((o) => o.organization_id === user.current_organization_id) || user?.organizations?.[0];
    return !!org && ADMIN_ROLES.includes((org.role || '').toLowerCase());
  }, [user]);

  const [filters, setFilters] = useState<UsageFilterState>(() => {
    const init = defaultFilters();
    return isAdmin && selfUserId ? { ...init, userId: selfUserId } : init;
  });
  // Tracks whether the one-time admin "default to Me" has been applied. Initializes to true
  // when the lazy useState initializer above already applied it (user available synchronously
  // on first render). Once applied, effectiveUserId stops substituting selfUserId and defers to
  // filters.userId verbatim — so an admin explicitly picking "All users" (empty string) sticks.
  const defaultAppliedRef = useRef(isAdmin && !!selfUserId);
  useEffect(() => {
    if (isAdmin && selfUserId && !defaultAppliedRef.current) {
      setFilters((f) => ({ ...f, userId: selfUserId }));
      defaultAppliedRef.current = true;
    }
  }, [isAdmin, selfUserId]);
  const [mode, setMode] = useState<'tokens' | 'usd'>('tokens');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const tz = browserTimeZone();
  const effectiveUserId = !defaultAppliedRef.current && isAdmin && selfUserId ? selfUserId : filters.userId;
  const query: UsageQuery = { from: filters.from, to: filters.to, tz, project_id: filters.projectId || undefined, user_id: effectiveUserId || undefined };

  const wallet = useQuery({ queryKey: ['usage', 'wallet'], queryFn: getWallet, staleTime: 60_000 });
  const projects = useQuery({ queryKey: ['projects', 'list'], queryFn: () => listProjects(true) });
  const summary = useQuery({ queryKey: ['usage', 'summary', query], queryFn: () => getUsageSummary(query), enabled: !authLoading });
  const series = useQuery({ queryKey: ['usage', 'timeseries', query], queryFn: () => getUsageTimeseries(query), enabled: !authLoading });
  const prompts = useInfiniteQuery({
    queryKey: ['usage', 'prompts', query],
    queryFn: ({ pageParam }) => listUsagePrompts({ ...query, cursor: pageParam as string | undefined, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !authLoading,
  });
  const detail = useQuery({ queryKey: ['usage', 'prompt', expandedId], queryFn: () => getUsagePrompt(expandedId!), enabled: !!expandedId });
  const linked = useQuery({ queryKey: ['usage', 'prompt', linkedPromptId], queryFn: () => getUsagePrompt(linkedPromptId!), enabled: !!linkedPromptId });

  const rows = prompts.data?.pages.flatMap((p) => p.items) ?? [];
  const hasError = wallet.isError || summary.isError || series.isError || prompts.isError;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Button component={RouterLink} to="/" startIcon={<BackIcon />} sx={{ mr: 2 }}>Chat</Button>
          <AgentIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" component="h1">Usage</Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={3}>
          {hasError && (
            <Alert severity="error">Some usage data couldn't be loaded. Try refreshing.</Alert>
          )}

          <WalletCard wallet={wallet.data} isLoading={wallet.isLoading} isError={wallet.isError} />

          {linkedPromptId && (
            <Card variant="outlined"><CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Linked prompt</Typography>
              <Box sx={{ '& .MuiIconButton-root': { pointerEvents: 'none', opacity: 0.5 } }}>
                <PromptsTable rows={linked.data?.prompt ? [linked.data.prompt] : []} showUser={isAdmin} expandedId={linkedPromptId}
                  onToggle={() => {}} detail={linked.data} detailLoading={linked.isLoading} hasMore={false} onLoadMore={() => {}} isLoading={linked.isLoading} />
              </Box>
            </CardContent></Card>
          )}

          <UsageFilters value={filters} onChange={setFilters} isAdmin={isAdmin} selfUserId={selfUserId}
            projects={(projects.data?.projects ?? []).map((p) => ({ id: p.id, name: p.name }))} />

          <Card><CardContent>
            <UsageChart points={series.data?.points ?? []} mode={mode} onModeChange={setMode} />
          </CardContent></Card>

          <SummaryTiles summary={summary.data} isLoading={summary.isLoading} isError={summary.isError} />

          <Card><CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Prompts</Typography>
            <PromptsTable rows={rows} showUser={isAdmin} expandedId={expandedId}
              onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
              detail={detail.data} detailLoading={detail.isLoading}
              hasMore={!!prompts.hasNextPage} onLoadMore={() => prompts.fetchNextPage()} isLoading={prompts.isLoading || prompts.isFetchingNextPage} />
          </CardContent></Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default UsagePage;
